import { useState, useEffect, useRef, useCallback } from 'react'
import { Table, Button, Badge, Modal, Form, Row, Col, Card, Alert, Tabs, Tab, InputGroup, Dropdown, ListGroup } from 'react-bootstrap'
import { toast } from 'react-toastify'
import { phonebookApi } from '../services/api'
import { useConfirm } from '../components/common/ConfirmDialog'
import { useAuthStore } from '../store/authStore'

interface PhonebookEntry {
  id: string
  display_name: string
  first_name?: string
  last_name?: string
  company?: string
  phone_work?: string
  phone_mobile?: string
  phone_home?: string
  email?: string
  address?: string
  city?: string
  postal_code?: string
  source_id: string
  source_name: string
  is_favorite?: boolean
  group?: string
  editable?: boolean
}

interface PhonebookSource {
  id: string
  name: string
  type: string
  priority: number
  is_active: boolean
  auto_sync: boolean
  sync_interval: number
  last_sync: string | null
  entry_count: number
  settings: Record<string, string>
  graph_cert_present?: boolean
}

interface SourceType {
  value: string
  label: string
  description: string
  icon: string
}

interface TelsearchResult {
  id: string
  display_name: string
  phone_work: string
  address: string
  city: string
  postal_code: string
}

interface DuplicateEntry {
  id: string
  display_name: string
  phone: string
  duplicate_of: string
  original_phone: string
}

interface DuplicateResult {
  source_id: string
  source_name: string
  total_entries: number
  unique_phones: number
  duplicates_found: number
  duplicates: DuplicateEntry[]
  dry_run: boolean
  deleted_count?: number
  message: string
}

interface TelsearchStats {
  cache: {
    total_entries: number
    found_entries: number
    not_found_entries: number
    expired_entries: number
    valid_entries: number
  }
  queries: {
    total_queries: number
    cache_hits: number
    internet_queries: number
    successful_lookups: number
    not_found: number
    api_errors: number
    auto_saved_count: number
    last_query_at: string | null
  }
  metrics: {
    cache_hit_rate_percent: number
    success_rate_percent: number
  }
}

interface TargetPhonebook {
  id: string
  name: string
  type: string
}

const emptyEntryForm = {
  display_name: '',
  first_name: '',
  last_name: '',
  company: '',
  phone_work: '',
  phone_mobile: '',
  phone_home: '',
  email: '',
  group: '',
  is_favorite: false
}

const emptySourceForm = {
  name: '',
  type: 'csv',
  priority: 10,
  is_active: true,
  auto_sync: false,
  sync_interval: 3600,
  settings: {} as Record<string, string>
}

export default function Phonebook() {
  const confirm = useConfirm()
  const { selectedTenant } = useAuthStore()
  const [entries, setEntries] = useState<PhonebookEntry[]>([])
  const [sources, setSources] = useState<PhonebookSource[]>([])
  const [sourceTypes, setSourceTypes] = useState<SourceType[]>([])
  const [groups, setGroups] = useState<string[]>([])
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [activeTab, setActiveTab] = useState('entries')

  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [selectedSource, setSelectedSource] = useState<string>('')
  const [selectedGroup, setSelectedGroup] = useState<string>('')
  const [favoritesOnly, setFavoritesOnly] = useState(false)

  const PAGE_SIZE = 100
  const [totalEntries, setTotalEntries] = useState(0)
  const [currentOffset, setCurrentOffset] = useState(0)

  const [showEntryModal, setShowEntryModal] = useState(false)
  const [editingEntry, setEditingEntry] = useState<PhonebookEntry | null>(null)
  const [entryForm, setEntryForm] = useState(emptyEntryForm)

  const [showSourceModal, setShowSourceModal] = useState(false)
  const [editingSource, setEditingSource] = useState<PhonebookSource | null>(null)
  const [sourceForm, setSourceForm] = useState(emptySourceForm)
  // Microsoft-Graph-Quelle: Ordner-Dropdown + Cert-Upload-Status
  const [graphFolders, setGraphFolders] = useState<Array<{ id: string; name: string }>>([])
  const [graphBusy, setGraphBusy] = useState(false)
  const [graphMsg, setGraphMsg] = useState<{ ok: boolean; text: string } | null>(null)
  const [graphCertInfo, setGraphCertInfo] = useState<{ thumbprint: string; not_after: string } | null>(null)

  const generateGraphCert = async () => {
    if (!editingSource) { setGraphMsg({ ok: false, text: 'Quelle zuerst speichern, dann Zertifikat erstellen.' }); return }
    setGraphBusy(true); setGraphMsg(null)
    try {
      const res = await phonebookApi.generateGraphCert(editingSource.id, { days: 3650 })
      setGraphCertInfo({ thumbprint: res.data.thumbprint, not_after: res.data.not_after })
      setSourceForm(f => ({ ...f, settings: { ...f.settings, _graph_cert_present: 'true' } }))
      setGraphMsg({ ok: true, text: 'Zertifikat erstellt. Jetzt \u201eÖffentliches Zertifikat herunterladen\u201c und in Entra hochladen.' })
    } catch {
      setGraphMsg({ ok: false, text: 'Zertifikat konnte nicht erstellt werden.' })
    } finally { setGraphBusy(false) }
  }

  const downloadGraphCert = async () => {
    if (!editingSource) return
    try {
      const res = await phonebookApi.downloadGraphCert(editingSource.id)
      const url = window.URL.createObjectURL(new Blob([res.data]))
      const a = document.createElement('a')
      a.href = url
      a.download = `graph-${editingSource.name}.crt`.replace(/\s+/g, '_')
      document.body.appendChild(a); a.click(); a.remove()
      window.URL.revokeObjectURL(url)
    } catch {
      setGraphMsg({ ok: false, text: 'Kein Zertifikat zum Herunterladen vorhanden.' })
    }
  }

  const uploadGraphCert = async (file: File) => {
    if (!editingSource) return
    setGraphBusy(true); setGraphMsg(null)
    try {
      await phonebookApi.uploadGraphCert(editingSource.id, file)
      setGraphMsg({ ok: true, text: 'Zertifikat gespeichert.' })
      setSourceForm(f => ({ ...f, settings: { ...f.settings, _graph_cert_present: 'true' } }))
    } catch {
      setGraphMsg({ ok: false, text: 'Zertifikat abgelehnt (PEM mit Key UND Zertifikat nötig).' })
    } finally { setGraphBusy(false) }
  }

  const loadGraphFolders = async () => {
    if (!editingSource) { setGraphMsg({ ok: false, text: 'Quelle zuerst speichern, dann Ordner laden.' }); return }
    setGraphBusy(true); setGraphMsg(null)
    try {
      const res = await phonebookApi.listGraphFolders(editingSource.id)
      setGraphFolders(res.data.items || [])
      if (!(res.data.items || []).length) setGraphMsg({ ok: false, text: 'Keine Kontaktordner gefunden.' })
    } catch (e: unknown) {
      const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      setGraphMsg({ ok: false, text: detail || 'Ordner konnten nicht geladen werden.' })
    } finally { setGraphBusy(false) }
  }

  const [showTelsearchModal, setShowTelsearchModal] = useState(false)
  const [telsearchQuery, setTelsearchQuery] = useState({ phone: '', name: '', city: '' })
  const [telsearchResults, setTelsearchResults] = useState<TelsearchResult[]>([])
  const [telsearchLoading, setTelsearchLoading] = useState(false)

  const [showDuplicateModal, setShowDuplicateModal] = useState(false)
  const [duplicateSourceId, setDuplicateSourceId] = useState<string>('')
  const [duplicateResult, setDuplicateResult] = useState<DuplicateResult | null>(null)
  const [duplicateLoading, setDuplicateLoading] = useState(false)

  const [telsearchStats, setTelsearchStats] = useState<TelsearchStats | null>(null)
  const [targetPhonebooks, setTargetPhonebooks] = useState<TargetPhonebook[]>([])

  const fileInputRef = useRef<HTMLInputElement>(null)
  const [uploadSourceId, setUploadSourceId] = useState<string>('')

  // Import preview state
  const [showImportPreview, setShowImportPreview] = useState(false)
  const [importPreviewLoading, setImportPreviewLoading] = useState(false)
  const [importFile, setImportFile] = useState<File | null>(null)
  const [importSourceId, setImportSourceId] = useState<string>('')
  const [previewHeaders, setPreviewHeaders] = useState<string[]>([])
  const [previewRows, setPreviewRows] = useState<string[][]>([])
  const [fieldMapping, setFieldMapping] = useState<Record<string, string>>({})
  const [targetFields, setTargetFields] = useState<Array<{value: string, label: string}>>([])
  const [importLoading, setImportLoading] = useState(false)

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300)
    return () => clearTimeout(timer)
  }, [search])

  useEffect(() => {
    loadData()
  }, [])

  useEffect(() => {
    setCurrentOffset(0)
    loadEntries(0)
  }, [debouncedSearch, selectedSource, selectedGroup, favoritesOnly])

  const loadData = async () => {
    try {
      const [sourcesRes, typesRes, groupsRes] = await Promise.all([
        phonebookApi.listSources(),
        phonebookApi.getSourceTypes(),
        phonebookApi.getGroups()
      ])
      setSources(sourcesRes.data.items || [])
      setSourceTypes(typesRes.data.types || [])
      setGroups(groupsRes.data.groups || [])
      await loadEntries(0)
      // Load Telsearch stats if there's a telsearch source
      const hasTelsearch = (sourcesRes.data.items || []).some((s: PhonebookSource) => s.type === 'telsearch')
      if (hasTelsearch) {
        loadTelsearchStats()
        loadTargetPhonebooks()
      }
    } catch {
      toast.error('Fehler beim Laden')
    } finally {
      setLoading(false)
    }
  }

  const loadTelsearchStats = async () => {
    try {
      const response = await phonebookApi.getTelsearchStats()
      setTelsearchStats(response.data)
    } catch {
      // Ignore - stats not critical
    }
  }

  const loadTargetPhonebooks = async () => {
    try {
      const response = await phonebookApi.getTargetPhonebooks()
      setTargetPhonebooks(response.data.items || [])
    } catch {
      // Ignore
    }
  }

  const resetStats = async () => {
    try {
      await phonebookApi.resetTelsearchStats()
      toast.success('Statistik zurückgesetzt')
      loadTelsearchStats()
    } catch {
      toast.error('Fehler beim Zurücksetzen')
    }
  }

  const loadEntries = useCallback(async (newOffset: number = 0) => {
    try {
      const params: Record<string, unknown> = { limit: PAGE_SIZE, offset: newOffset }
      if (debouncedSearch) params.search = debouncedSearch
      if (selectedSource) params.source_id = selectedSource
      if (selectedGroup) params.group = selectedGroup
      if (favoritesOnly) params.favorites_only = true
      const response = await phonebookApi.list(params)
      const newItems = response.data.items || []
      setTotalEntries(response.data.total || 0)
      if (newOffset === 0) {
        setEntries(newItems)
      } else {
        setEntries(prev => [...prev, ...newItems])
      }
      setCurrentOffset(newOffset)
    } catch {
      // Ignore
    }
  }, [debouncedSearch, selectedSource, selectedGroup, favoritesOnly])

  const loadMore = async () => {
    const nextOffset = currentOffset + PAGE_SIZE
    setLoadingMore(true)
    await loadEntries(nextOffset)
    setLoadingMore(false)
  }

  const hasMore = entries.length < totalEntries

  const openCreateEntryModal = () => {
    setEditingEntry(null)
    setEntryForm(emptyEntryForm)
    setShowEntryModal(true)
  }

  const openEditEntryModal = (entry: PhonebookEntry) => {
    setEditingEntry(entry)
    setEntryForm({
      display_name: entry.display_name || '',
      first_name: entry.first_name || '',
      last_name: entry.last_name || '',
      company: entry.company || '',
      phone_work: entry.phone_work || '',
      phone_mobile: entry.phone_mobile || '',
      phone_home: entry.phone_home || '',
      email: entry.email || '',
      group: entry.group || '',
      is_favorite: entry.is_favorite || false
    })
    setShowEntryModal(true)
  }

  const handleEntrySubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      if (editingEntry) {
        await phonebookApi.update(editingEntry.id, entryForm)
        toast.success('Eintrag aktualisiert')
      } else {
        await phonebookApi.create(entryForm)
        toast.success('Eintrag erstellt')
      }
      setShowEntryModal(false)
      loadEntries()
      loadData()
    } catch {
      toast.error('Fehler beim Speichern')
    }
  }

  const handleEntryDelete = async (entry: PhonebookEntry) => {
    if (!entry.editable) {
      toast.error('Dieser Eintrag kann nicht gelöscht werden')
      return
    }
    const confirmed = await confirm({
      title: 'Telefonbuch-Eintrag löschen',
      message: `Eintrag "${entry.display_name}" wirklich löschen?`,
      confirmText: 'Löschen',
      variant: 'danger'
    })
    if (!confirmed) return
    try {
      await phonebookApi.delete(entry.id)
      toast.success('Eintrag gelöscht')
      loadEntries()
    } catch {
      toast.error('Fehler beim Löschen')
    }
  }

  const openCreateSourceModal = () => {
    setEditingSource(null)
    setSourceForm(emptySourceForm)
    setGraphFolders([]); setGraphMsg(null); setGraphCertInfo(null)
    setShowSourceModal(true)
  }

  const openEditSourceModal = (source: PhonebookSource) => {
    setEditingSource(source)
    setSourceForm({
      name: source.name,
      type: source.type,
      priority: source.priority,
      is_active: source.is_active,
      auto_sync: source.auto_sync,
      sync_interval: source.sync_interval,
      settings: source.settings || {}
    })
    setGraphFolders([]); setGraphMsg(null); setGraphCertInfo(null)
    setShowSourceModal(true)
  }

  const handleSourceSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      if (editingSource) {
        await phonebookApi.updateSource(editingSource.id, sourceForm)
        toast.success('Quelle aktualisiert')
      } else {
        await phonebookApi.createSource(sourceForm)
        toast.success('Quelle erstellt')
      }
      setShowSourceModal(false)
      loadData()
    } catch {
      toast.error('Fehler beim Speichern')
    }
  }

  const handleSourceDelete = async (source: PhonebookSource) => {
    if (source.type === 'internal' || source.type === 'manual') {
      toast.error('System-Quellen können nicht gelöscht werden')
      return
    }
    const confirmed = await confirm({
      title: 'Telefonbuch-Quelle löschen',
      message: `Quelle "${source.name}" wirklich löschen?`,
      confirmText: 'Löschen',
      variant: 'danger'
    })
    if (!confirmed) return
    try {
      await phonebookApi.deleteSource(source.id)
      toast.success('Quelle gelöscht')
      loadData()
    } catch {
      toast.error('Fehler beim Löschen')
    }
  }

  const syncSource = async (source: PhonebookSource) => {
    try {
      const response = await phonebookApi.syncSource(source.id)
      toast.success(`${response.data.entry_count} Einträge synchronisiert`)
      loadData()
    } catch {
      toast.error('Fehler bei der Synchronisierung')
    }
  }

  const triggerFileUpload = (sourceId: string) => {
    setUploadSourceId(sourceId)
    fileInputRef.current?.click()
  }

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file || !uploadSourceId) return

    const filename = file.name.toLowerCase()
    if (filename.endsWith('.csv') || filename.endsWith('.xlsx')) {
      // Show preview for CSV/XLSX
      setImportFile(file)
      setImportSourceId(uploadSourceId)
      setImportPreviewLoading(true)
      setShowImportPreview(true)
      try {
        const response = await phonebookApi.previewSourceFile(uploadSourceId, file)
        setPreviewHeaders(response.data.headers || [])
        setPreviewRows(response.data.sample_rows || [])
        setFieldMapping(response.data.auto_mapping || {})
        setTargetFields(response.data.target_fields || [])
      } catch {
        toast.error('Fehler beim Laden der Vorschau')
        setShowImportPreview(false)
      } finally {
        setImportPreviewLoading(false)
      }
    } else {
      // Direct upload for XML
      try {
        const response = await phonebookApi.uploadSourceFile(uploadSourceId, file)
        toast.success(response.data.message)
        loadData()
      } catch {
        toast.error('Fehler beim Upload')
      }
    }
    if (fileInputRef.current) fileInputRef.current.value = ''
    setUploadSourceId('')
  }

  const handleMappedImport = async () => {
    if (!importFile || !importSourceId) return
    setImportLoading(true)
    try {
      const response = await phonebookApi.importMappedFile(importSourceId, importFile, fieldMapping)
      toast.success(response.data.message)
      setShowImportPreview(false)
      setImportFile(null)
      loadData()
    } catch {
      toast.error('Fehler beim Import')
    } finally {
      setImportLoading(false)
    }
  }

  const updateFieldMapping = (colIndex: string, fieldName: string) => {
    setFieldMapping(prev => ({ ...prev, [colIndex]: fieldName }))
  }

  const moveSourcePriority = async (sourceId: string, direction: 'up' | 'down') => {
    const sortedSources = [...sources].sort((a, b) => a.priority - b.priority)
    const idx = sortedSources.findIndex(s => s.id === sourceId)
    if (idx === -1) return
    const newIdx = direction === 'up' ? idx - 1 : idx + 1
    if (newIdx < 0 || newIdx >= sortedSources.length) return
    const priorities = sortedSources.map((s, i) => ({
      id: s.id,
      priority: i === idx ? sortedSources[newIdx].priority : (i === newIdx ? sortedSources[idx].priority : s.priority)
    }))
    try {
      await phonebookApi.reorderSources(priorities)
      loadData()
    } catch {
      toast.error('Fehler beim Sortieren')
    }
  }

  const searchTelsearch = async () => {
    if (!telsearchQuery.phone && !telsearchQuery.name) {
      toast.error('Bitte Telefonnummer oder Name eingeben')
      return
    }
    setTelsearchLoading(true)
    try {
      const response = await phonebookApi.lookupTelsearch(telsearchQuery)
      setTelsearchResults(response.data.results || [])
      if (response.data.results?.length === 0) {
        toast.info('Keine Ergebnisse gefunden')
      }
    } catch {
      toast.error('Fehler bei der Suche')
    } finally {
      setTelsearchLoading(false)
    }
  }

  const addTelsearchResult = async (result: TelsearchResult) => {
    try {
      await phonebookApi.create({
        display_name: result.display_name,
        phone_work: result.phone_work,
        group: 'Telsearch'
      })
      toast.success('Eintrag hinzugefügt')
      loadEntries()
    } catch {
      toast.error('Fehler beim Hinzufügen')
    }
  }

  const openDuplicateModal = async (sourceId: string) => {
    setDuplicateSourceId(sourceId)
    setDuplicateResult(null)
    setShowDuplicateModal(true)
    setDuplicateLoading(true)
    try {
      const response = await phonebookApi.cleanupDuplicates(sourceId, true)
      setDuplicateResult(response.data)
    } catch {
      toast.error('Fehler beim Prüfen auf Duplikate')
      setShowDuplicateModal(false)
    } finally {
      setDuplicateLoading(false)
    }
  }

  const confirmDeleteDuplicates = async () => {
    if (!duplicateSourceId) return
    setDuplicateLoading(true)
    try {
      const response = await phonebookApi.cleanupDuplicates(duplicateSourceId, false)
      setDuplicateResult(response.data)
      toast.success(response.data.message)
      loadData()
    } catch {
      toast.error('Fehler beim Löschen der Duplikate')
    } finally {
      setDuplicateLoading(false)
    }
  }

  const exportYealinkXml = async () => {
    try {
      const response = await phonebookApi.exportYealinkXml(selectedSource || undefined)
      const blob = new Blob([response.data], { type: 'application/xml' })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'phonebook.xml'
      a.click()
      window.URL.revokeObjectURL(url)
    } catch {
      toast.error('Fehler beim Export')
    }
  }

  const exportCsv = async () => {
    try {
      const response = await phonebookApi.exportCsv(selectedSource || undefined)
      const blob = new Blob([response.data], { type: 'text/csv' })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'phonebook.csv'
      a.click()
      window.URL.revokeObjectURL(url)
    } catch {
      toast.error('Fehler beim Export')
    }
  }

  const getSourceTypeLabel = (type: string) => sourceTypes.find(t => t.value === type)?.label || type
  const getSourceTypeIcon = (type: string) => sourceTypes.find(t => t.value === type)?.icon || 'bi-folder'

  const getSettingsFields = (type: string) => {
    switch (type) {
      case 'http': return [{ key: 'url', label: 'URL', type: 'url', placeholder: 'https://example.com/phonebook.xml' }]
      case 'ftp': return [
        { key: 'host', label: 'Host', type: 'text', placeholder: 'ftp.example.com' },
        { key: 'path', label: 'Pfad', type: 'text', placeholder: '/phonebook/contacts.csv' },
        { key: 'username', label: 'Benutzername', type: 'text', placeholder: '' },
        { key: 'password', label: 'Passwort', type: 'password', placeholder: '' }
      ]
      case 'carddav_client': return [
        { key: 'url', label: 'CardDAV URL', type: 'url', placeholder: 'https://cloud.example.com/remote.php/dav/addressbooks/user/contacts/' },
        { key: 'username', label: 'Benutzername', type: 'text', placeholder: '' },
        { key: 'password', label: 'Passwort', type: 'password', placeholder: '' }
      ]
      case 'telsearch': return [
        { key: 'api_key', label: 'API-Key (optional)', type: 'text', placeholder: 'Für erweiterte Abfragen' },
        { key: 'cache_days', label: 'Cache-Dauer (Tage)', type: 'number', placeholder: '30' },
        { key: 'auto_save_to_phonebook', label: 'Automatisch ins Telefonbuch speichern', type: 'checkbox', placeholder: '' },
        { key: 'target_source_id', label: 'Ziel-Telefonbuch für Auto-Save', type: 'select', placeholder: '', options: targetPhonebooks }
      ]
      case 'ldap_source': return [
        { key: 'server', label: 'LDAP Server', type: 'text', placeholder: 'ldap://ldap.example.com' },
        { key: 'base_dn', label: 'Base DN', type: 'text', placeholder: 'ou=contacts,dc=example,dc=com' },
        { key: 'bind_dn', label: 'Bind DN', type: 'text', placeholder: '' },
        { key: 'bind_password', label: 'Bind Passwort', type: 'password', placeholder: '' }
      ]
      case 'graph_shared': return [
        { key: 'tenant_id', label: 'Entra Tenant-ID (GUID)', type: 'text', placeholder: 'z. B. 8f2c…-…' },
        { key: 'client_id', label: 'App-Registrierung (Client-ID)', type: 'text', placeholder: 'z. B. 1a2b…' },
        { key: 'mailbox', label: 'Geteiltes Postfach (E-Mail/UPN)', type: 'text', placeholder: 'kontakte@firma.ch' }
      ]
      default: return []
    }
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h4>Telefonbuch</h4>
          <small className="text-muted">{entries.length}{totalEntries > entries.length ? ` von ${totalEntries}` : ''} Einträge aus {sources.filter(s => s.is_active).length} Quellen</small>
        </div>
        <div>
          <Dropdown className="d-inline-block me-2">
            <Dropdown.Toggle variant="outline-secondary" size="sm">
              <i className="bi bi-download me-1"></i> Export
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item onClick={exportYealinkXml}><i className="bi bi-file-earmark-code me-2"></i> Yealink XML</Dropdown.Item>
              <Dropdown.Item onClick={exportCsv}><i className="bi bi-file-earmark-text me-2"></i> CSV</Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
          <Button variant="outline-info" size="sm" className="me-2" onClick={() => setShowTelsearchModal(true)}>
            <i className="bi bi-search me-1"></i> Telsearch.ch
          </Button>
          <Button variant="primary" onClick={openCreateEntryModal}>
            <i className="bi bi-plus-lg me-1"></i> Neuer Eintrag
          </Button>
        </div>
      </div>

      <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k || 'entries')} className="mb-3">
        <Tab eventKey="entries" title={<><i className="bi bi-people me-1"></i> Einträge</>}>
          <Card className="mb-3">
            <Card.Body className="py-2">
              <Row className="align-items-center">
                <Col md={4}>
                  <InputGroup size="sm">
                    <InputGroup.Text><i className="bi bi-search"></i></InputGroup.Text>
                    <Form.Control placeholder="Suchen..." value={search} onChange={(e) => setSearch(e.target.value)} />
                  </InputGroup>
                </Col>
                <Col md={3}>
                  <Form.Select size="sm" value={selectedSource} onChange={(e) => setSelectedSource(e.target.value)}>
                    <option value="">Alle Quellen</option>
                    {sources.filter(s => s.is_active).map(s => (
                      <option key={s.id} value={s.id}>{s.name} ({s.entry_count})</option>
                    ))}
                  </Form.Select>
                </Col>
                <Col md={3}>
                  <Form.Select size="sm" value={selectedGroup} onChange={(e) => setSelectedGroup(e.target.value)}>
                    <option value="">Alle Gruppen</option>
                    {groups.map(g => <option key={g} value={g}>{g}</option>)}
                  </Form.Select>
                </Col>
                <Col md={2}>
                  <Form.Check type="switch" id="favorites" label="Favoriten" checked={favoritesOnly} onChange={(e) => setFavoritesOnly(e.target.checked)} />
                </Col>
              </Row>
            </Card.Body>
          </Card>

          <div className="table-container">
            <Table hover className="mb-0">
              <thead>
                <tr>
                  <th></th>
                  <th>Name</th>
                  <th>Firma</th>
                  <th>Telefon (Arbeit)</th>
                  <th>Mobil</th>
                  <th>Quelle</th>
                  <th className="text-end">Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={7} className="text-center py-4">Laden...</td></tr>
                ) : entries.length === 0 ? (
                  <tr><td colSpan={7} className="text-center py-4 text-muted">Keine Einträge gefunden</td></tr>
                ) : entries.map(entry => {
                  // Source badge colors
                  const sourceColors: Record<string, string> = {
                    'manual': 'secondary',
                    'telsearch': 'info',
                    'internal': 'primary',
                    'csv_import': 'warning',
                    'ldap': 'dark',
                  }
                  const sourceBg = sourceColors[entry.source_id] || 'light'

                  return (
                    <tr key={entry.id}>
                      <td style={{ width: 30 }}>{entry.is_favorite && <i className="bi bi-star-fill text-warning"></i>}</td>
                      <td>
                        <strong>{entry.display_name}</strong>
                        {entry.group && <Badge bg="secondary" className="ms-2">{entry.group}</Badge>}
                        {entry.city && <small className="text-muted d-block">{[entry.address, entry.postal_code, entry.city].filter(Boolean).join(', ')}</small>}
                      </td>
                      <td>{entry.company || '-'}</td>
                      <td>{entry.phone_work && <a href={`tel:${entry.phone_work}`} className="text-decoration-none"><i className="bi bi-telephone me-1"></i>{entry.phone_work}</a>}</td>
                      <td>{entry.phone_mobile && <a href={`tel:${entry.phone_mobile}`} className="text-decoration-none"><i className="bi bi-phone me-1"></i>{entry.phone_mobile}</a>}</td>
                      <td><Badge bg={sourceBg}>{entry.source_name}</Badge></td>
                      <td className="text-end">
                        {entry.editable && (
                          <>
                            <Button variant="outline-primary" size="sm" className="me-1" onClick={() => openEditEntryModal(entry)}><i className="bi bi-pencil"></i></Button>
                            <Button variant="outline-danger" size="sm" onClick={() => handleEntryDelete(entry)}><i className="bi bi-trash"></i></Button>
                          </>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </Table>
          </div>
          {hasMore && (
            <div className="text-center py-3">
              <Button variant="outline-primary" size="sm" onClick={loadMore} disabled={loadingMore}>
                {loadingMore ? (
                  <><i className="bi bi-hourglass-split me-1"></i>Laden...</>
                ) : (
                  <><i className="bi bi-arrow-down-circle me-1"></i>Weitere laden ({totalEntries - entries.length} verbleibend)</>
                )}
              </Button>
            </div>
          )}
        </Tab>

        <Tab eventKey="sources" title={<><i className="bi bi-database me-1"></i> Quellen</>}>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <small className="text-muted">Quellen werden nach Priorität sortiert zusammengeführt</small>
            <Button variant="primary" size="sm" onClick={openCreateSourceModal}><i className="bi bi-plus-lg me-1"></i> Neue Quelle</Button>
          </div>

          <input type="file" ref={fileInputRef} style={{ display: 'none' }} accept=".csv,.xlsx,.xml" onChange={handleFileUpload} />

          <ListGroup>
            {sources.sort((a, b) => a.priority - b.priority).map((source, idx) => (
              <ListGroup.Item key={source.id} className="d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center">
                  <div className="me-3 text-muted" style={{ width: 40 }}>
                    <Button variant="link" size="sm" className="p-0" disabled={idx === 0} onClick={() => moveSourcePriority(source.id, 'up')}><i className="bi bi-chevron-up"></i></Button>
                    <Button variant="link" size="sm" className="p-0" disabled={idx === sources.length - 1} onClick={() => moveSourcePriority(source.id, 'down')}><i className="bi bi-chevron-down"></i></Button>
                  </div>
                  <div>
                    <i className={`bi ${getSourceTypeIcon(source.type)} me-2`}></i>
                    <strong>{source.name}</strong>
                    <Badge bg={source.is_active ? 'success' : 'secondary'} className="ms-2">{source.is_active ? 'Aktiv' : 'Inaktiv'}</Badge>
                    <Badge bg="primary" className="ms-2">{source.entry_count} Einträge</Badge>
                    <br /><small className="text-muted">{getSourceTypeLabel(source.type)}{source.last_sync && ` \u2022 Zuletzt: ${new Date(source.last_sync).toLocaleString('de-DE')}`}</small>
                  </div>
                </div>
                <div>
                  {['csv', 'xlsx', 'xml_yealink', 'xml_generic'].includes(source.type) && (
                    <Button variant="outline-success" size="sm" className="me-1" onClick={() => triggerFileUpload(source.id)} title="Datei hochladen"><i className="bi bi-upload"></i></Button>
                  )}
                  {['http', 'ftp', 'carddav_client', 'internal'].includes(source.type) && (
                    <Button variant="outline-info" size="sm" className="me-1" onClick={() => syncSource(source)} title="Synchronisieren"><i className="bi bi-arrow-repeat"></i></Button>
                  )}
                  {source.entry_count > 0 && (
                    <Button variant="outline-warning" size="sm" className="me-1" onClick={() => openDuplicateModal(source.id)} title="Duplikate entfernen"><i className="bi bi-layers-half"></i></Button>
                  )}
                  <Button variant="outline-primary" size="sm" className="me-1" onClick={() => openEditSourceModal(source)}><i className="bi bi-pencil"></i></Button>
                  {!['internal', 'manual'].includes(source.type) && (
                    <Button variant="outline-danger" size="sm" onClick={() => handleSourceDelete(source)}><i className="bi bi-trash"></i></Button>
                  )}
                </div>
              </ListGroup.Item>
            ))}
          </ListGroup>

          <Alert variant="info" className="mt-3">
            <i className="bi bi-info-circle me-2"></i>
            <strong>LDAP-Zugriff:</strong> Das Telefonbuch kann über LDAP abgerufen werden: <code>ldap://[SERVER-IP]:389</code> oder <code>ldaps://[SERVER-IP]:636</code>
          </Alert>

          <Alert variant="success" className="mt-2">
            <i className="bi bi-phone me-2"></i>
            <strong>CardDAV-Zugriff:</strong> Das Telefonbuch kann über CardDAV abgerufen werden (z.B. für iOS, macOS, Android):
            <br />
            <code>https://[SERVER-IP]/carddav/{selectedTenant?.domain || '[TENANT-DOMAIN]'}/addressbook/</code>
            <br />
            <small className="text-muted">Authentifizierung mit SIP-Zugangsdaten: Benutzername = <code>{selectedTenant?.domain || '[DOMAIN]'}_[NEBENSTELLE]</code>, Passwort = SIP-Passwort</small>
          </Alert>

          {/* Telsearch Statistics */}
          {telsearchStats && sources.some(s => s.type === 'telsearch') && (
            <Card className="mt-3">
              <Card.Header className="d-flex justify-content-between align-items-center">
                <span><i className="bi bi-graph-up me-2"></i>Telsearch Statistik</span>
                <Button variant="outline-secondary" size="sm" onClick={resetStats} title="Statistik zurücksetzen">
                  <i className="bi bi-arrow-counterclockwise"></i>
                </Button>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <h6 className="text-muted mb-3">Abfragen</h6>
                    <Table size="sm" borderless>
                      <tbody>
                        <tr>
                          <td>Gesamtabfragen</td>
                          <td className="text-end fw-bold">{telsearchStats.queries.total_queries}</td>
                        </tr>
                        <tr>
                          <td><i className="bi bi-lightning-charge text-success me-1"></i>Cache-Treffer</td>
                          <td className="text-end text-success">{telsearchStats.queries.cache_hits}</td>
                        </tr>
                        <tr>
                          <td><i className="bi bi-globe text-primary me-1"></i>Internet-Abfragen</td>
                          <td className="text-end text-primary">{telsearchStats.queries.internet_queries}</td>
                        </tr>
                        <tr>
                          <td><i className="bi bi-check-circle text-success me-1"></i>Erfolgreich gefunden</td>
                          <td className="text-end text-success">{telsearchStats.queries.successful_lookups}</td>
                        </tr>
                        <tr>
                          <td><i className="bi bi-x-circle text-warning me-1"></i>Nicht gefunden</td>
                          <td className="text-end text-warning">{telsearchStats.queries.not_found}</td>
                        </tr>
                        <tr>
                          <td><i className="bi bi-exclamation-triangle text-danger me-1"></i>API-Fehler</td>
                          <td className="text-end text-danger">{telsearchStats.queries.api_errors}</td>
                        </tr>
                        <tr>
                          <td><i className="bi bi-save text-info me-1"></i>Auto-gespeichert</td>
                          <td className="text-end text-info">{telsearchStats.queries.auto_saved_count}</td>
                        </tr>
                      </tbody>
                    </Table>
                  </Col>
                  <Col md={6}>
                    <h6 className="text-muted mb-3">Cache</h6>
                    <Table size="sm" borderless>
                      <tbody>
                        <tr>
                          <td>Einträge gesamt</td>
                          <td className="text-end fw-bold">{telsearchStats.cache.total_entries}</td>
                        </tr>
                        <tr>
                          <td>Gefunden</td>
                          <td className="text-end text-success">{telsearchStats.cache.found_entries}</td>
                        </tr>
                        <tr>
                          <td>Nicht gefunden</td>
                          <td className="text-end text-muted">{telsearchStats.cache.not_found_entries}</td>
                        </tr>
                        <tr>
                          <td>Gültig</td>
                          <td className="text-end text-success">{telsearchStats.cache.valid_entries}</td>
                        </tr>
                        <tr>
                          <td>Abgelaufen</td>
                          <td className="text-end text-warning">{telsearchStats.cache.expired_entries}</td>
                        </tr>
                      </tbody>
                    </Table>
                    <hr />
                    <h6 className="text-muted mb-2">Effizienz</h6>
                    <div className="d-flex justify-content-between">
                      <span>Cache-Trefferquote:</span>
                      <Badge bg={telsearchStats.metrics.cache_hit_rate_percent > 50 ? 'success' : 'warning'}>
                        {telsearchStats.metrics.cache_hit_rate_percent}%
                      </Badge>
                    </div>
                    <div className="d-flex justify-content-between mt-1">
                      <span>Erfolgsquote:</span>
                      <Badge bg={telsearchStats.metrics.success_rate_percent > 50 ? 'success' : 'warning'}>
                        {telsearchStats.metrics.success_rate_percent}%
                      </Badge>
                    </div>
                    {telsearchStats.queries.last_query_at && (
                      <div className="mt-2 text-muted small">
                        Letzte Abfrage: {new Date(telsearchStats.queries.last_query_at).toLocaleString('de-DE')}
                      </div>
                    )}
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          )}
        </Tab>
      </Tabs>

      {/* Entry Modal */}
      <Modal show={showEntryModal} onHide={() => setShowEntryModal(false)} size="lg">
        <Modal.Header closeButton><Modal.Title>{editingEntry ? 'Eintrag bearbeiten' : 'Neuer Eintrag'}</Modal.Title></Modal.Header>
        <Form onSubmit={handleEntrySubmit}>
          <Modal.Body>
            <Row>
              <Col md={6}><Form.Group className="mb-3"><Form.Label>Anzeigename *</Form.Label><Form.Control value={entryForm.display_name} onChange={(e) => setEntryForm({ ...entryForm, display_name: e.target.value })} required /></Form.Group></Col>
              <Col md={6}><Form.Group className="mb-3"><Form.Label>Firma</Form.Label><Form.Control value={entryForm.company} onChange={(e) => setEntryForm({ ...entryForm, company: e.target.value })} /></Form.Group></Col>
            </Row>
            <Row>
              <Col md={4}><Form.Group className="mb-3"><Form.Label>Vorname</Form.Label><Form.Control value={entryForm.first_name} onChange={(e) => setEntryForm({ ...entryForm, first_name: e.target.value })} /></Form.Group></Col>
              <Col md={4}><Form.Group className="mb-3"><Form.Label>Nachname</Form.Label><Form.Control value={entryForm.last_name} onChange={(e) => setEntryForm({ ...entryForm, last_name: e.target.value })} /></Form.Group></Col>
              <Col md={4}><Form.Group className="mb-3"><Form.Label>Gruppe</Form.Label><Form.Control value={entryForm.group} onChange={(e) => setEntryForm({ ...entryForm, group: e.target.value })} placeholder="z.B. Kunden" list="groups-list" /><datalist id="groups-list">{groups.map(g => <option key={g} value={g} />)}</datalist></Form.Group></Col>
            </Row>
            <Row>
              <Col md={4}><Form.Group className="mb-3"><Form.Label>Telefon (Arbeit)</Form.Label><Form.Control value={entryForm.phone_work} onChange={(e) => setEntryForm({ ...entryForm, phone_work: e.target.value })} /></Form.Group></Col>
              <Col md={4}><Form.Group className="mb-3"><Form.Label>Mobil</Form.Label><Form.Control value={entryForm.phone_mobile} onChange={(e) => setEntryForm({ ...entryForm, phone_mobile: e.target.value })} /></Form.Group></Col>
              <Col md={4}><Form.Group className="mb-3"><Form.Label>Privat</Form.Label><Form.Control value={entryForm.phone_home} onChange={(e) => setEntryForm({ ...entryForm, phone_home: e.target.value })} /></Form.Group></Col>
            </Row>
            <Row>
              <Col md={8}><Form.Group className="mb-3"><Form.Label>E-Mail</Form.Label><Form.Control type="email" value={entryForm.email} onChange={(e) => setEntryForm({ ...entryForm, email: e.target.value })} /></Form.Group></Col>
              <Col md={4}><Form.Group className="mb-3 pt-4"><Form.Check type="switch" id="is_favorite" label="Favorit" checked={entryForm.is_favorite} onChange={(e) => setEntryForm({ ...entryForm, is_favorite: e.target.checked })} /></Form.Group></Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowEntryModal(false)}>Abbrechen</Button>
            <Button variant="primary" type="submit">{editingEntry ? 'Speichern' : 'Erstellen'}</Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Source Modal */}
      <Modal show={showSourceModal} onHide={() => setShowSourceModal(false)} size="lg">
        <Modal.Header closeButton><Modal.Title>{editingSource ? 'Quelle bearbeiten' : 'Neue Quelle'}</Modal.Title></Modal.Header>
        <Form onSubmit={handleSourceSubmit}>
          <Modal.Body>
            <Row>
              <Col md={6}><Form.Group className="mb-3"><Form.Label>Name *</Form.Label><Form.Control value={sourceForm.name} onChange={(e) => setSourceForm({ ...sourceForm, name: e.target.value })} required placeholder="z.B. Geschäftskontakte" /></Form.Group></Col>
              <Col md={6}><Form.Group className="mb-3"><Form.Label>Typ *</Form.Label><Form.Select value={sourceForm.type} onChange={(e) => setSourceForm({ ...sourceForm, type: e.target.value, settings: {} })} disabled={!!editingSource}>{sourceTypes.filter(t => !['internal', 'manual'].includes(t.value)).map(t => <option key={t.value} value={t.value}>{t.label} - {t.description}</option>)}</Form.Select></Form.Group></Col>
            </Row>
            <Row>
              <Col md={4}><Form.Group className="mb-3"><Form.Label>Priorität</Form.Label><Form.Control type="number" min={1} max={100} value={sourceForm.priority} onChange={(e) => setSourceForm({ ...sourceForm, priority: parseInt(e.target.value) || 10 })} /><Form.Text className="text-muted">Niedrigere Werte = höhere Priorität</Form.Text></Form.Group></Col>
              <Col md={4}><Form.Group className="mb-3"><Form.Label>Sync-Intervall</Form.Label><Form.Select value={sourceForm.sync_interval} onChange={(e) => setSourceForm({ ...sourceForm, sync_interval: parseInt(e.target.value) })}><option value={0}>Nur manuell</option><option value={300}>Alle 5 Minuten</option><option value={900}>Alle 15 Minuten</option><option value={3600}>Stündlich</option><option value={86400}>Täglich</option></Form.Select></Form.Group></Col>
              <Col md={4}><Form.Group className="mb-3 pt-4"><Form.Check type="switch" id="source_active" label="Aktiv" checked={sourceForm.is_active} onChange={(e) => setSourceForm({ ...sourceForm, is_active: e.target.checked })} /></Form.Group></Col>
            </Row>
            {getSettingsFields(sourceForm.type).length > 0 && (
              <>
                <hr />
                <h6>Einstellungen für {getSourceTypeLabel(sourceForm.type)}</h6>
                {getSettingsFields(sourceForm.type).map(field => (
                  <Form.Group className="mb-3" key={field.key}>
                    {field.type === 'checkbox' ? (
                      <Form.Check
                        type="switch"
                        id={`setting-${field.key}`}
                        label={field.label}
                        checked={sourceForm.settings[field.key] === 'true'}
                        onChange={(e) => setSourceForm({
                          ...sourceForm,
                          settings: { ...sourceForm.settings, [field.key]: e.target.checked ? 'true' : 'false' }
                        })}
                      />
                    ) : field.type === 'select' && field.options ? (
                      <>
                        <Form.Label>{field.label}</Form.Label>
                        <Form.Select
                          value={sourceForm.settings[field.key] || ''}
                          onChange={(e) => setSourceForm({
                            ...sourceForm,
                            settings: { ...sourceForm.settings, [field.key]: e.target.value }
                          })}
                        >
                          <option value="">-- Standard (Telsearch) --</option>
                          {field.options.map((opt: TargetPhonebook) => (
                            <option key={opt.id} value={opt.id}>{opt.name}</option>
                          ))}
                        </Form.Select>
                      </>
                    ) : (
                      <>
                        <Form.Label>{field.label}</Form.Label>
                        <Form.Control
                          type={field.type}
                          value={sourceForm.settings[field.key] || ''}
                          onChange={(e) => setSourceForm({
                            ...sourceForm,
                            settings: { ...sourceForm.settings, [field.key]: e.target.value }
                          })}
                          placeholder={field.placeholder}
                        />
                      </>
                    )}
                  </Form.Group>
                ))}
              </>
            )}
            {sourceForm.type === 'graph_shared' && (
              <>
                <hr />
                <h6>Microsoft-Graph-Verbindung</h6>
                <Form.Group className="mb-3">
                  <Form.Label>Authentifizierung</Form.Label>
                  <Form.Select
                    value={sourceForm.settings.auth || 'certificate'}
                    onChange={(e) => setSourceForm({ ...sourceForm, settings: { ...sourceForm.settings, auth: e.target.value } })}
                  >
                    <option value="certificate">Zertifikat (empfohlen, lange Laufzeit)</option>
                    <option value="federated">Federated Credential (Workload Identity)</option>
                  </Form.Select>
                </Form.Group>

                {(sourceForm.settings.auth || 'certificate') === 'certificate' ? (
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Zertifikat (PEM mit privatem Schlüssel UND Zertifikat){' '}
                      {editingSource?.graph_cert_present && <span className="text-success">— hinterlegt ✓</span>}
                    </Form.Label>
                    <Form.Control
                      type="file"
                      accept=".pem,.key,.crt"
                      disabled={!editingSource || graphBusy}
                      onChange={(e) => { const f = (e.target as HTMLInputElement).files?.[0]; if (f) uploadGraphCert(f) }}
                    />
                    <Form.Text className="text-muted">
                      {editingSource ? 'Der private Schlüssel bleibt auf dem Server (0600), er wird nie über die API zurückgegeben.'
                                     : 'Quelle zuerst erstellen, dann Zertifikat erstellen oder hochladen.'}
                    </Form.Text>
                    <div className="d-flex gap-2 mt-2">
                      <Button variant="outline-primary" size="sm" onClick={generateGraphCert} disabled={!editingSource || graphBusy}>
                        <i className="bi bi-shield-lock me-1"></i>{graphBusy ? '…' : 'Zertifikat erstellen (10 Jahre)'}
                      </Button>
                      <Button variant="outline-secondary" size="sm" onClick={downloadGraphCert} disabled={!editingSource || !(editingSource?.graph_cert_present || graphCertInfo)}>
                        <i className="bi bi-download me-1"></i>Öffentliches Zertifikat (.crt)
                      </Button>
                    </div>
                    {graphCertInfo && (
                      <div className="mt-2" style={{ fontSize: '0.8rem' }}>
                        <div>Thumbprint (SHA-1): <code>{graphCertInfo.thumbprint}</code></div>
                        <div className="text-muted">Gültig bis {graphCertInfo.not_after}. Öffentliches Zertifikat herunterladen und in der Entra-App-Registrierung hochladen.</div>
                      </div>
                    )}
                  </Form.Group>
                ) : (
                  <Form.Group className="mb-3">
                    <Form.Text className="text-muted">
                      Federated Credential: Die App-Registrierung vertraut einem externen OIDC-Aussteller — nichts läuft ab.
                      Das Assertion-Token wird serverseitig bezogen (Aussteller-abhängig).
                    </Form.Text>
                  </Form.Group>
                )}

                <Form.Group className="mb-3">
                  <Form.Label>Kontaktordner</Form.Label>
                  <div className="d-flex gap-2">
                    <Form.Select
                      value={sourceForm.settings.folder_id || ''}
                      onChange={(e) => setSourceForm({ ...sourceForm, settings: { ...sourceForm.settings, folder_id: e.target.value } })}
                    >
                      <option value="">-- Ordner wählen --</option>
                      {sourceForm.settings.folder_id && !graphFolders.some(f => f.id === sourceForm.settings.folder_id) && (
                        <option value={sourceForm.settings.folder_id}>(aktuell gewählt)</option>
                      )}
                      {graphFolders.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                    </Form.Select>
                    <Button variant="outline-secondary" onClick={loadGraphFolders} disabled={graphBusy}>
                      {graphBusy ? '…' : 'Ordner laden'}
                    </Button>
                  </div>
                </Form.Group>

                {graphMsg && (
                  <div className={graphMsg.ok ? 'text-success' : 'text-danger'} style={{ fontSize: '0.85rem' }}>
                    {graphMsg.text}
                  </div>
                )}
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowSourceModal(false)}>Abbrechen</Button>
            <Button variant="primary" type="submit">{editingSource ? 'Speichern' : 'Erstellen'}</Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Telsearch Modal */}
      <Modal show={showTelsearchModal} onHide={() => setShowTelsearchModal(false)} size="lg">
        <Modal.Header closeButton><Modal.Title><i className="bi bi-search me-2"></i>Telsearch.ch - Schweizer Telefonverzeichnis</Modal.Title></Modal.Header>
        <Modal.Body>
          <Row className="mb-3">
            <Col md={4}><Form.Group><Form.Label>Telefonnummer</Form.Label><Form.Control value={telsearchQuery.phone} onChange={(e) => setTelsearchQuery({ ...telsearchQuery, phone: e.target.value })} placeholder="+41..." /></Form.Group></Col>
            <Col md={4}><Form.Group><Form.Label>Name</Form.Label><Form.Control value={telsearchQuery.name} onChange={(e) => setTelsearchQuery({ ...telsearchQuery, name: e.target.value })} placeholder="Müller" /></Form.Group></Col>
            <Col md={4}><Form.Group><Form.Label>Ort</Form.Label><Form.Control value={telsearchQuery.city} onChange={(e) => setTelsearchQuery({ ...telsearchQuery, city: e.target.value })} placeholder="Zürich" /></Form.Group></Col>
          </Row>
          <Button variant="primary" onClick={searchTelsearch} disabled={telsearchLoading} className="mb-3">
            {telsearchLoading ? <><i className="bi bi-hourglass-split me-1"></i> Suche...</> : <><i className="bi bi-search me-1"></i> Suchen</>}
          </Button>
          {telsearchResults.length > 0 && (
            <Table hover size="sm">
              <thead><tr><th>Name</th><th>Telefon</th><th>Adresse</th><th></th></tr></thead>
              <tbody>
                {telsearchResults.map(result => (
                  <tr key={result.id}>
                    <td>{result.display_name}</td>
                    <td>{result.phone_work}</td>
                    <td>{[result.address, result.postal_code, result.city].filter(Boolean).join(', ')}</td>
                    <td><Button variant="outline-success" size="sm" onClick={() => addTelsearchResult(result)}><i className="bi bi-plus"></i> Hinzufügen</Button></td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Modal.Body>
        <Modal.Footer><Button variant="secondary" onClick={() => setShowTelsearchModal(false)}>Schließen</Button></Modal.Footer>
      </Modal>

      {/* Duplicate Cleanup Modal */}
      <Modal show={showDuplicateModal} onHide={() => setShowDuplicateModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title><i className="bi bi-layers-half me-2"></i>Duplikate entfernen</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {duplicateLoading && !duplicateResult && (
            <div className="text-center py-4">
              <i className="bi bi-hourglass-split fs-1 text-muted"></i>
              <p className="mt-2">Suche nach Duplikaten...</p>
            </div>
          )}
          {duplicateResult && (
            <>
              <Alert variant={duplicateResult.duplicates_found > 0 ? 'warning' : 'success'}>
                <strong>{duplicateResult.source_name}</strong>: {duplicateResult.total_entries} Einträge, {duplicateResult.unique_phones} eindeutige Telefonnummern
                {duplicateResult.duplicates_found > 0 && (
                  <><br /><strong>{duplicateResult.duplicates_found} Duplikate gefunden</strong></>
                )}
                {duplicateResult.deleted_count !== undefined && (
                  <><br /><i className="bi bi-check-circle text-success me-1"></i>{duplicateResult.deleted_count} Duplikate wurden gelöscht</>
                )}
              </Alert>
              {duplicateResult.duplicates_found > 0 && duplicateResult.dry_run && (
                <>
                  <p className="text-muted">Folgende Einträge werden gelöscht (behalten wird jeweils der erste Eintrag mit dieser Nummer):</p>
                  <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                    <Table hover size="sm">
                      <thead><tr><th>Name</th><th>Telefon</th><th>Duplikat von</th></tr></thead>
                      <tbody>
                        {duplicateResult.duplicates.map((dup, idx) => (
                          <tr key={idx}>
                            <td>{dup.display_name}</td>
                            <td>{dup.phone}</td>
                            <td><Badge bg="secondary">{dup.duplicate_of}</Badge></td>
                          </tr>
                        ))}
                        {duplicateResult.duplicates_found > duplicateResult.duplicates.length && (
                          <tr><td colSpan={3} className="text-center text-muted">... und {duplicateResult.duplicates_found - duplicateResult.duplicates.length} weitere</td></tr>
                        )}
                      </tbody>
                    </Table>
                  </div>
                </>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDuplicateModal(false)}>Schließen</Button>
          {duplicateResult && duplicateResult.duplicates_found > 0 && duplicateResult.dry_run && (
            <Button variant="danger" onClick={confirmDeleteDuplicates} disabled={duplicateLoading}>
              {duplicateLoading ? <><i className="bi bi-hourglass-split me-1"></i> Lösche...</> : <><i className="bi bi-trash me-1"></i> {duplicateResult.duplicates_found} Duplikate löschen</>}
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      {/* Import Preview Modal */}
      <Modal show={showImportPreview} onHide={() => { setShowImportPreview(false); setImportFile(null) }} size="xl">
        <Modal.Header closeButton>
          <Modal.Title><i className="bi bi-table me-2"></i>Import-Vorschau & Feld-Zuordnung</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {importPreviewLoading ? (
            <div className="text-center py-4"><i className="bi bi-hourglass-split me-2"></i>Datei wird analysiert...</div>
          ) : (
            <>
              <Alert variant="info" className="py-2">
                <i className="bi bi-info-circle me-1"></i>
                Ordnen Sie die Spalten den Zielfeldern zu. Nicht benötigte Spalten auf &quot;Ignorieren&quot; setzen.
              </Alert>

              <div className="table-responsive" style={{ maxHeight: '60vh', overflow: 'auto' }}>
                <Table bordered size="sm" className="mb-0">
                  <thead className="table-light" style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                    <tr>
                      {previewHeaders.map((header, idx) => (
                        <th key={idx} style={{ minWidth: 160 }}>
                          <div className="mb-1 text-truncate" title={header}><small className="text-muted">{header}</small></div>
                          <Form.Select
                            size="sm"
                            value={fieldMapping[String(idx)] || ''}
                            onChange={(e) => updateFieldMapping(String(idx), e.target.value)}
                          >
                            {targetFields.map(f => (
                              <option key={f.value} value={f.value}>{f.label}</option>
                            ))}
                          </Form.Select>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {previewRows.map((row, rowIdx) => (
                      <tr key={rowIdx}>
                        {previewHeaders.map((_, colIdx) => (
                          <td key={colIdx} className="text-truncate" style={{ maxWidth: 200 }} title={row[colIdx] || ''}>
                            <small>{row[colIdx] || ''}</small>
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>

              <div className="mt-3 d-flex align-items-center">
                <small className="text-muted">
                  <i className="bi bi-eye me-1"></i>
                  Vorschau: {previewRows.length} Zeilen angezeigt
                  {' \u2022 '}
                  Zugeordnete Felder: {Object.values(fieldMapping).filter(v => v).length} von {previewHeaders.length}
                </small>
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => { setShowImportPreview(false); setImportFile(null) }}>Abbrechen</Button>
          <Button
            variant="primary"
            onClick={handleMappedImport}
            disabled={importLoading || importPreviewLoading || Object.values(fieldMapping).filter(v => v).length === 0}
          >
            {importLoading ? <><i className="bi bi-hourglass-split me-1"></i>Importiere...</> : <><i className="bi bi-check-lg me-1"></i>Importieren</>}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}
